import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CatalogUnprocessedEntitiesClient } from '@backstage/plugin-catalog-unprocessed-entities-common';
import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
  Link,
} from '@backstage/core-components';
import { Chip, Typography } from '@material-ui/core';
import {
  computeCatalogHealth,
  computeUnprocessedHealth,
  CatalogHealthEntry,
} from './checks';
import { domainConfig } from '../home/DomainPage';
import { categories } from '../home/HomePage';

const columns: TableColumn<CatalogHealthEntry>[] = [
  {
    title: 'Type',
    render: entry => entry.entity.kind,
  },
  {
    title: 'Nom',
    render: entry => {
      const { kind, metadata } = entry.entity;
      if (entry.unprocessed) {
        return metadata.name;
      }
      const namespace = metadata.namespace || 'default';
      return (
        <Link
          to={`/catalog/${namespace}/${kind.toLowerCase()}/${metadata.name}`}
        >
          {(metadata.title as string) || metadata.name}
        </Link>
      );
    },
  },
  {
    title: 'Problèmes',
    render: entry => (
      <>
        {entry.issues.map((issue, index) => (
          <Chip
            key={`${issue.code}-${index}`}
            label={issue.label}
            size="small"
            style={{ margin: 2 }}
          />
        ))}
      </>
    ),
  },
];

export const CatalogHealthPage = () => {
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const unprocessedApi = useMemo(
    () => new CatalogUnprocessedEntitiesClient(discoveryApi, fetchApi),
    [discoveryApi, fetchApi],
  );
  const [health, setHealth] = useState<
    { results: CatalogHealthEntry[]; total: number } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    Promise.all([catalogApi.getEntities(), unprocessedApi.failed()])
      .then(([{ items }, { entities: failed }]) =>
        setHealth({
          results: [
            ...computeUnprocessedHealth(failed),
            ...computeCatalogHealth(items, domainConfig, categories),
          ],
          total: items.length + failed.length,
        }),
      )
      .catch(e => setError(e));
  }, [catalogApi, unprocessedApi]);

  if (error) {
    return (
      <Page themeId="home">
        <Header title="État du catalogue" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  if (!health) {
    return (
      <Page themeId="home">
        <Header title="État du catalogue" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  const { results, total } = health;

  return (
    <Page themeId="home">
      <Header
        title="État du catalogue"
        subtitle="Entités du catalogue avec un problème de configuration"
      />
      <Content>
        {results.length === 0 ? (
          <Typography variant="body1">
            Aucune entité mal configurée détectée.
          </Typography>
        ) : (
          <Table<CatalogHealthEntry>
            title={`${results.length} entités mal configurées sur ${total}`}
            columns={columns}
            data={results}
            options={{ paging: results.length > 20, search: true }}
          />
        )}
      </Content>
    </Page>
  );
};
